import React from "react";
import { Link } from "react-router-dom";
import { Atom, Rocket, Sparkle, Planet, Path, ShieldCheck, ArrowRight, Play } from "@phosphor-icons/react";
import TopNav from "@/components/TopNav";

const GALAXY_BG = "https://images.pexels.com/photos/33441873/pexels-photo-33441873.jpeg";
const SPIRAL_BG = "https://images.pexels.com/photos/37761980/pexels-photo-37761980.jpeg";

const FEATURES = [
  { icon: Planet, title: "Interactive Solar System", text: "Orbit, zoom and select every planet and moon in a living 3D model rendered in real time." },
  { icon: Sparkle, title: "Astrophysical Analysis", text: "Open a rich, structured analysis for any world, star or nebula — grounded in verified astronomy." },
  { icon: Path, title: "Voice-Narrated Journeys", text: "Curated guided tours with immersive voice narration — from the Grand Tour to the search for ocean worlds." },
  { icon: ShieldCheck, title: "Live Sky Tonight", text: "Real planet positions for tonight from your own location, plus NASA-fact-sheet-accurate data." },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen">
      <TopNav />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={GALAXY_BG} alt="cosmic nebula" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #030305 10%, rgba(3,3,5,0.6) 60%, rgba(3,3,5,0.85) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, #030305 0%, transparent 40%)" }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 w-full pt-16">
          <div className="max-w-2xl fade-up">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-cyan-400 pulse-dot" />
              <span className="text-tel text-[10px] text-cyan-400/80">Intergalactic Exploration Platform · v1</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Navigate the<br />known <span className="text-cyan-400">universe.</span>
            </h1>
            <p className="text-white/60 text-lg mt-7 leading-relaxed max-w-xl">
              A cinematic, real-time map of the solar system, galaxy and beyond — built for explorers,
              educators, and the future of aerospace.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link
                to="/explore"
                data-testid="cta-explore"
                className="flex items-center gap-2.5 px-7 py-3.5 rounded-full font-semibold bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300 transition-colors"
              >
                <Rocket size={20} weight="fill" /> Launch Explorer
              </Link>
              <Link
                to="/demo"
                data-testid="cta-demo"
                className="flex items-center gap-2.5 px-7 py-3.5 rounded-full font-semibold border border-white/20 text-white hover:border-cyan-400/60 hover:text-cyan-200 transition-colors"
              >
                <Play size={18} weight="fill" /> Investor Demo
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 z-10 max-w-6xl mx-auto px-6 sm:px-10 flex gap-8 sm:gap-16">
          {[["80+", "Celestial Objects"], ["4", "Guided Journeys"], ["∞", "Cosmic Scale"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-display font-bold text-3xl text-white">{n}</div>
              <div className="text-tel text-[9px] text-white/45 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative max-w-6xl mx-auto px-6 sm:px-10 py-28">
        <span className="text-tel text-[10px] text-cyan-400/80">The Platform</span>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter mt-3 max-w-2xl leading-tight">
          Exploration, intelligence and storytelling in one interface.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-14">
          {FEATURES.map((f, i) => (
            <div key={i} className="glass rounded-2xl p-8 hover:border-cyan-400/30 transition-colors" data-testid={`feature-${i}`}>
              <f.icon size={30} weight="duotone" color="#00D8FF" />
              <h3 className="font-display font-bold text-xl tracking-tight mt-5">{f.title}</h3>
              <p className="text-white/55 text-sm mt-3 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={SPIRAL_BG} alt="spiral galaxy" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, #030305 0%, rgba(3,3,5,0.7) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-32 text-center">
          <Atom size={40} weight="duotone" color="#00D8FF" className="mx-auto" />
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-6 leading-tight">
            The cosmos is waiting.
          </h2>
          <p className="text-white/60 mt-5 max-w-lg mx-auto">Step into the platform investors and educators are calling the next frontier of exploration software.</p>
          <Link
            to="/explore"
            data-testid="cta-explore-bottom"
            className="inline-flex items-center gap-2.5 mt-9 px-8 py-4 rounded-full font-semibold bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300 transition-colors"
          >
            Begin Exploring <ArrowRight size={20} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/8 py-8 text-center">
        <span className="text-tel text-[9px] text-white/30">STELLARNAV · Educational prototype · Data based on NASA fact sheets</span>
      </footer>
    </div>
  );
}
